import type { ScoringInstructionsState, ScoreRange } from "../types/ScoringInstruction.type";

export interface ValidationResult {
	errors: Record<string, string>;
	isValid: boolean;
	warnings: string[];
}

export function validateScoringInstructions(state: ScoringInstructionsState): ValidationResult {
	const errors: Record<string, string> = {};
	const warnings: string[] = [];

	// Rule 1: totalScore must be > 0
	if (state.totalScore <= 0) {
		errors["total"] = "Total score must be greater than 0";
	}

	// Rule 6: At least one range required
	if (state.ranges.length === 0) {
		errors["ranges"] = "At least one score range is required";
		return { isValid: false, errors, warnings };
	}

	// Validate individual ranges
	for (const range of state.ranges) {
		// Rule 2: min >= 0
		if (range.min < 0) {
			errors[range.id] = "Minimum score cannot be negative";
			continue;
		}

		// Rule 2: max <= totalScore
		if (range.max > state.totalScore) {
			errors[range.id] = `Maximum score cannot exceed total score (${state.totalScore})`;
			continue;
		}

		// Rule 2: min <= max
		if (range.min > range.max) {
			errors[range.id] = "Minimum score cannot be greater than maximum score";
			continue;
		}

		// Rule 3: single-value check
		if (range.isSingleValue && range.min !== range.max) {
			errors[range.id] = "Single value range must have equal min and max";
			continue;
		}
	}

	// Rules 4 & 5: Check for gaps and overlaps using sorted ranges
	const sorted = [...state.ranges].sort((a, b) => a.min - b.min);

	// Check coverage starts at 0
	if (sorted.length > 0 && sorted[0].min !== 0) {
		warnings.push(`Score range should start at 0, but starts at ${sorted[0].min}`);
	}

	// Check for gaps and overlaps between consecutive ranges
	for (let i = 1; i < sorted.length; i++) {
		const prev = sorted[i - 1];
		const curr = sorted[i];

		if (curr.min > prev.max + 1) {
			warnings.push(`Gap detected between ${prev.max} and ${curr.min}`);
		}

		if (curr.min <= prev.max) {
			const overlapId = curr.id;
			errors[overlapId] = `Overlaps with previous range (${prev.min}-${prev.max})`;
		}
	}

	// Check coverage ends at totalScore
	if (sorted.length > 0 && state.totalScore > 0) {
		const lastRange = sorted[sorted.length - 1];
		if (lastRange.max !== state.totalScore) {
			warnings.push(`Score range should end at ${state.totalScore}, but ends at ${lastRange.max}`);
		}
	}

	const isValid = Object.keys(errors).length === 0;
	return { isValid, errors, warnings };
}
